import React from 'react';
import { Target, TrendingUp, ShieldCheck, AlertCircle, ArrowLeft } from 'lucide-react';

interface Props {
  level: 1 | 2 | 3;
  percentage: number;
  label?: string;
  description?: string;
  onClick?: () => void;
}

export default function ProfitThermometer({ level, percentage, label, description, onClick }: Props) {
  const getLevelInfo = () => {
    switch(level) {
      case 1:
        return {
          title: label || "Preço Perigoso!",
          description: description || "Faltam custos fixos no seu cálculo.",
          color: "text-rose-600",
          bgColor: "bg-rose-100",
          barColor: "bg-rose-500",
          icon: <AlertCircle className="h-5 w-5 text-rose-600" />
        };
      case 2:
        return {
          title: label || "Quase lá!",
          description: description || "Configuração financeira em andamento.",
          color: "text-amber-600",
          bgColor: "bg-amber-100",
          barColor: "bg-amber-500",
          icon: <Target className="h-5 w-5 text-amber-600" />
        };
      case 3:
      default:
        return {
          title: label || "Preço Seguro!",
          description: description || "Lucro Real Garantido.",
          color: "text-brand",
          bgColor: "bg-brand/10",
          barColor: "bg-brand",
          icon: <ShieldCheck className="h-5 w-5 text-brand" />
        };
    }
  };

  const info = getLevelInfo();

  if (level === 1) {
    return (
      <div 
        onClick={onClick}
        className={`bg-white rounded-2xl p-4 shadow-sm border border-zinc-100 border-l-4 border-l-rose-500 relative overflow-hidden group hover:border-zinc-200 transition-all active:scale-[0.98] ${onClick ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-center gap-3 relative z-10">
          <div className="p-2 rounded-xl bg-rose-50 text-rose-600 shrink-0">
            <AlertCircle className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-black text-sm text-zinc-900 leading-tight">
              {info.title}
            </h3>
            <p className="text-zinc-500 text-[11px] leading-snug mt-0.5">
              {info.description}
            </p>
          </div>
          <div className="text-[11px] font-bold text-rose-600 shrink-0 flex items-center gap-1 group-hover:translate-x-0.5 transition-transform ml-2">
            <span>Corrigir custos</span>
            <ArrowLeft className="h-3 w-3 rotate-180" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      onClick={onClick}
      className={`bg-white rounded-3xl p-5 shadow-sm border border-zinc-100 relative overflow-hidden group hover:border-brand/20 transition-all active:scale-[0.98] ${onClick ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start gap-4 mb-4 relative z-10">
        <div className={`p-3 rounded-2xl ${info.bgColor} shadow-inner`}>
          {info.icon}
        </div>
        <div className="flex-1">
          <div className="flex justify-between items-center">
            <h3 className={`font-black text-lg ${info.color}`}>{info.title}</h3>
            <span className={`text-[10px] font-black ${info.color} bg-white/50 px-2 py-0.5 rounded-lg border border-current/10`}>
              {Math.round(percentage)}%
            </span>
          </div>
          <p className="text-zinc-500 text-xs leading-relaxed mt-1">{info.description}</p>
        </div>
      </div>

      <div className="relative z-10 mt-6">
        <div className="flex justify-between items-end mb-2">
          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Diagnóstico de Saúde</span>
        </div>
        
        {/* Thermometer Bar */}
        <div className="h-4 w-full bg-zinc-100 rounded-full overflow-hidden flex p-0.5 border border-zinc-200/50">
          <div 
            className={`h-full ${info.barColor} rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(0,0,0,0.1)]`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        
        {/* Level markers */}
        <div className="flex justify-between mt-2 text-[10px] font-bold text-zinc-400">
          <span>0%</span>
          <span className={percentage >= 50 ? 'text-amber-500' : ''}>50%</span>
          <span className={percentage >= 100 ? 'text-brand' : ''}>100%</span>
        </div>
      </div>
      
      <div className="absolute top-2 right-4">
        <ArrowLeft className="h-4 w-4 text-zinc-200 rotate-180 group-hover:text-brand transition-colors" />
      </div>

      {/* Background decoration */}
      <div className={`absolute -bottom-10 -right-10 w-40 h-40 ${info.bgColor} rounded-full blur-3xl opacity-30 pointer-events-none group-hover:opacity-50 transition-opacity`} />
    </div>
  );
}
